import { useEffect } from "react";

const DEFAULT_OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/DffEGtuvcsZNRrxReXHneg3algZ2/social-images/social-1773176422282-Executive_Sprinter_Vans_(1).webp";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string;
  jsonLd?: object;
  image?: string;
}

export const SEOHead = ({ title, description, canonical, jsonLd, image }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const ogImage = image || DEFAULT_OG_IMAGE;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", `https://vauren.com${canonical}`, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:image", ogImage);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `https://vauren.com${canonical}`;

    // JSON-LD
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (existingScript) existingScript.remove();

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.querySelector('script[data-seo-jsonld]');
      if (s) s.remove();
    };
  }, [title, description, canonical, jsonLd]);

  return null;
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Vauren",
  description: "Vauren is a premium luxury van platform — rent self-drive vans from verified hosts, host your van to earn, manage a fleet, or commission a custom build.",
  url: "https://vauren.com",
  telephone: "+1-469-729-0024",
  email: "info@vauren.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Dallas",
    addressRegion: "TX",
    addressCountry: "US",
  },
  areaServed: [
    { "@type": "City", name: "Dallas" },
    { "@type": "City", name: "Fort Worth" },
    { "@type": "City", name: "Houston" },
    { "@type": "City", name: "Austin" },
    { "@type": "City", name: "Miami" },
    { "@type": "City", name: "Los Angeles" },
    { "@type": "City", name: "New York" },
    { "@type": "City", name: "Las Vegas" },
    { "@type": "City", name: "Chicago" },
    { "@type": "City", name: "Atlanta" },
    { "@type": "City", name: "Nashville" },
    { "@type": "City", name: "Plano" },
    { "@type": "City", name: "Frisco" },
    { "@type": "City", name: "Arlington" },
  ],
  priceRange: "$$$$",
  openingHours: "Mo-Su 00:00-23:59",
};

export const serviceSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url: `https://vauren.com${url}`,
  provider: {
    "@type": "LocalBusiness",
    name: "Vauren",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Dallas",
      addressRegion: "TX",
    },
  },
  areaServed: {
    "@type": "State",
    name: "Texas",
  },
});
